import { useState } from 'react';

/**
 * Multiple choice input for Menti participants.
 * Renders a grid of large tap-friendly buttons.
 * Tapping an option immediately submits the answer.
 *
 * @param {Object} props
 * @param {Object} props.slide - Slide data with config.options array
 * @param {function} props.onSubmit - Called with the selected option index
 * @param {boolean} props.loading
 */
export default function MultipleChoiceInput({ slide, onSubmit, loading }) {
  const [selected, setSelected] = useState(null);
  const options = slide.config?.options || [];

  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  // Blue color variants for each option
  const colorVariants = [
    'bg-blue-500 hover:bg-blue-400 active:bg-blue-600',
    'bg-blue-600 hover:bg-blue-500 active:bg-blue-700',
    'bg-indigo-500 hover:bg-indigo-400 active:bg-indigo-600',
    'bg-sky-500 hover:bg-sky-400 active:bg-sky-600',
    'bg-cyan-600 hover:bg-cyan-500 active:bg-cyan-700',
    'bg-blue-700 hover:bg-blue-600 active:bg-blue-800',
    'bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700',
    'bg-sky-600 hover:bg-sky-500 active:bg-sky-700',
  ];

  const handleSelect = (index) => {
    if (loading || selected !== null) return;
    setSelected(index);
    onSubmit(index);
  };

  return (
    <div className="flex flex-col gap-3">
      {options.map((option, index) => {
        const isSelected = selected === index;
        const colorClass = colorVariants[index % colorVariants.length];

        return (
          <button
            key={index}
            onClick={() => handleSelect(index)}
            disabled={loading || selected !== null}
            className={`
              w-full min-h-[60px] px-5 py-4 rounded-2xl text-white font-bold text-lg
              flex items-center gap-4 text-left
              shadow-lg transition-all duration-200
              ${isSelected
                ? 'bg-green-500 ring-4 ring-green-300/50 scale-[0.98]'
                : colorClass
              }
              ${selected !== null && !isSelected ? 'opacity-40' : ''}
              disabled:hover:translate-y-0 disabled:hover:shadow-lg
              hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0
            `}
          >
            <span className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-xl font-extrabold shrink-0">
              {letters[index]}
            </span>
            <span className="flex-1">{typeof option === 'string' ? option : option.text || option.label || `Alternativ ${letters[index]}`}</span>
            {isSelected && (
              <span className="material-symbols-outlined text-2xl shrink-0">check_circle</span>
            )}
          </button>
        );
      })}

      {loading && selected !== null && (
        <div className="flex justify-center mt-2">
          <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}
